import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Mail, MessageSquare, Phone, MapPin } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { MrAvatar } from '../components/MrAvatar';
/*
  SEO SPECIFICATION:
  Title: Contact MindStream.ing — Talk to MR Directly | MindStream.ing
  Meta Desc: Say hey to MISTR. Fill out the form and get a response in under 2 minutes. No gatekeeping. No waiting. Strategy call included with every inquiry.
  Primary KW: contact MindStream.ing
*/
export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    businessName: '',
    email: '',
    phone: '',
    fix: 'Too many tasks falling through cracks'
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission logic would go here
    console.log('Form submitted:', formData);
  };
  return <div className="min-h-screen flex flex-col" data-id="element-1465">
      <Helmet data-id="element-1466">
        <title data-id="element-1467">Contact MindStream.ing | Say Hey to MISTR</title>
        <meta name="description" content="Say hey to MISTR. Fill out the form and get a response in under 2 minutes. No gatekeeping. No waiting. Strategy call included with every inquiry." data-id="element-1468" />
      </Helmet>

      <main className="pt-20 flex-grow" data-id="element-1469">
        {/* 1. Hero Section */}
        <section className="relative py-24 px-6 bg-hero-gradient overflow-hidden" data-id="element-1470">
          {/* Ambient Glow Orbs */}
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-1471" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1472" />

          {/* Grid Pattern Overlay */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }} data-id="element-1473" />

          <div className="inner-container relative z-10 flex flex-col items-center justify-center py-8" data-id="element-1474">
            <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="mb-6" data-id="element-1475">
              <MrAvatar size={100} expression="winking" animate={true} data-id="element-1476" />
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="font-poppins font-extrabold text-5xl md:text-6xl leading-[1.1] text-center" data-id="element-1477">
              Say Hey to <span className="text-gradient" data-id="element-1478">MISTR.</span>
            </motion.h1>
          </div>
        </section>

        {/* 2. Content Section */}
        <section className="py-20 px-6 relative bg-white" data-id="element-1479">
          <div className="inner-container max-w-6xl mx-auto" data-id="element-1480">
            {/* Las Vegas Image - Full Width */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="rounded-2xl overflow-hidden border border-gray-200 mb-12 shadow-lg" data-id="element-1481">
              <img src="https://adroimarketing.com/wp-content/uploads/2021/09/LasVegasMarketingCompany-1024x683.jpg" alt="Las Vegas Strip at night" className="w-full object-contain" data-id="element-1482" />
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-12" data-id="element-1483">
              {/* Left Column - Form */}
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.5
            }} className="bg-dark-bg backdrop-blur-md border border-white/10 rounded-2xl p-8 shadow-xl" data-id="element-1484">
                <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1485">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1486">
                    <div data-id="element-1487">
                      <label className="block text-sm font-medium text-steel mb-2" data-id="element-1488">
                        Name
                      </label>
                      <input type="text" required className="w-full bg-dark-bg/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple focus:bg-white/5 transition-colors" value={formData.name} onChange={e => setFormData({
                      ...formData,
                      name: e.target.value
                    })} data-id="element-1489" />
                    </div>
                    <div data-id="element-1490">
                      <label className="block text-sm font-medium text-steel mb-2" data-id="element-1491">
                        Business Name
                      </label>
                      <input type="text" required className="w-full bg-dark-bg/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple focus:bg-white/5 transition-colors" value={formData.businessName} onChange={e => setFormData({
                      ...formData,
                      businessName: e.target.value
                    })} data-id="element-1492" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1493">
                    <div data-id="element-1494">
                      <label className="block text-sm font-medium text-steel mb-2" data-id="element-1495">
                        Email
                      </label>
                      <input type="email" required className="w-full bg-dark-bg/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple focus:bg-white/5 transition-colors" value={formData.email} onChange={e => setFormData({
                      ...formData,
                      email: e.target.value
                    })} data-id="element-1496" />
                    </div>
                    <div data-id="element-1497">
                      <label className="block text-sm font-medium text-steel mb-2" data-id="element-1498">
                        Phone
                      </label>
                      <input type="tel" className="w-full bg-dark-bg/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple focus:bg-white/5 transition-colors" value={formData.phone} onChange={e => setFormData({
                      ...formData,
                      phone: e.target.value
                    })} data-id="element-1499" />
                    </div>
                  </div>

                  <div data-id="element-1500">
                    <label className="block text-sm font-medium text-steel mb-2" data-id="element-1501">
                      What are you trying to fix?
                    </label>
                    <select className="w-full bg-dark-bg/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple focus:bg-white/5 transition-colors appearance-none" value={formData.fix} onChange={e => setFormData({
                    ...formData,
                    fix: e.target.value
                  })} data-id="element-1502">
                      <option data-id="element-1503">Too many tasks falling through cracks</option>
                      <option data-id="element-1504">Can't scale without hiring</option>
                      <option data-id="element-1505">Marketing is inconsistent</option>
                      <option data-id="element-1506">Need a CRM that actually works</option>
                      <option data-id="element-1507">Everything — I need the full ecosystem</option>
                      <option data-id="element-1508">Other</option>
                    </select>
                  </div>

                  <button type="submit" className="btn-primary w-full py-4 text-lg mt-4 flex items-center justify-center gap-2" data-id="element-1509">
                    <MessageSquare size={20} data-id="element-1510" />
                    Talk to MR →
                  </button>
                </form>
              </motion.div>

              {/* Right Column - Contact Info */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="flex flex-col gap-4" data-id="element-1511">
                {/* Phone */}
                <div className="bg-dark-bg border border-white/10 rounded-xl p-6 flex items-center gap-5 hover:border-white/20 transition-colors" data-id="element-1512">
                  <div className="bg-white/10 p-4 rounded-full text-white shrink-0" data-id="element-1513">
                    <Phone size={24} data-id="element-1514" />
                  </div>
                  <div data-id="element-1515">
                    <p className="text-sm text-steel mb-1" data-id="element-1516">Local Phone</p>
                    <a href="tel:17022134801" className="font-poppins font-semibold text-lg text-white hover:text-teal transition-colors" data-id="element-1517">
                      1 (702) 213-4801
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="bg-dark-bg border border-white/10 rounded-xl p-6 flex items-center gap-5 hover:border-white/20 transition-colors" data-id="element-1518">
                  <div className="bg-white/10 p-4 rounded-full text-white shrink-0" data-id="element-1519">
                    <Mail size={24} data-id="element-1520" />
                  </div>
                  <div data-id="element-1521">
                    <p className="text-sm text-steel mb-1" data-id="element-1522">Email</p>
                    <a href="mailto:hello@mindstream.ing" className="font-poppins font-semibold text-lg text-white hover:text-teal transition-colors" data-id="element-1523">
                      hello@mindstream.ing
                    </a>
                  </div>
                </div>

                {/* Schedule a Meeting */}
                <div className="bg-dark-bg border border-white/10 rounded-xl p-6 flex items-center gap-5 hover:border-white/20 transition-colors" data-id="element-1524">
                  <div className="bg-white/10 p-4 rounded-full text-white shrink-0" data-id="element-1525">
                    <Calendar size={24} data-id="element-1526" />
                  </div>
                  <div data-id="element-1527">
                    <p className="text-sm text-steel mb-1" data-id="element-1528">
                      Schedule a Meeting
                    </p>
                    <button className="font-poppins font-semibold text-lg text-white hover:text-teal transition-colors text-left" data-id="element-1529">
                      Click Here to Book A Meeting
                    </button>
                  </div>
                </div>

                {/* Address */}
                <div className="bg-dark-bg border border-white/10 rounded-xl p-6 flex items-center gap-5 hover:border-white/20 transition-colors" data-id="element-1530">
                  <div className="bg-white/10 p-4 rounded-full text-white shrink-0" data-id="element-1531">
                    <MapPin size={24} data-id="element-1532" />
                  </div>
                  <div data-id="element-1533">
                    <p className="text-sm text-steel mb-1" data-id="element-1534">Address</p>
                    <p className="font-poppins font-medium text-white leading-relaxed" data-id="element-1535">
                      4730 S. Fort Apache Rd., Suite 300
                      <br data-id="element-1536" />
                      Las Vegas, NV 89147
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
    </div>;
}